import { useEffect, useMemo, useRef, useState } from "react";
import {
  Star, ArrowLeft, Calendar, User, CheckCircle2,
  Zap, ExternalLink, BookOpen, MessageSquare, ChevronDown,
  ThumbsUp, Terminal, Copy, Check, MoreHorizontal, Pencil, Trash2,
  KeyRound, AlertTriangle, Globe, Link2,
} from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { getMcpBySlug } from "../data/mcpRegistry";
import type { RegistryDetails } from "../lib/registryApi";
import {
  usePostRegistryComment,
  useRegistryDetails,
  useToggleRegistryStar,
  useEditRegistryComment,
  useDeleteRegistryComment,
} from "../lib/registryQueries";
import { resolveHostedMcpBrowserLink } from "../api";
import type { McpEntry } from "../data/mcpRegistry";
import { fetchSessionUser, type SessionUser } from "../lib/sessionUser";
import TopHeader from "../components/TopHeader";
import Footer from "../components/Footer";
import RegistryConnectModal from "../components/RegistryConnectModal";

function resolveHostedMcpHref(
  details: RegistryDetails | null,
  fallback: McpEntry | undefined,
): string | null {
  const path = details?.linkOnlyUrl ?? fallback?.linkOnlyUrl;
  if (!path) return null;
  return resolveHostedMcpBrowserLink(path);
}

const CATEGORY_COLORS: Record<string, string> = {
  productivity:  "bg-blue-50 text-blue-600 border-blue-100 dark:bg-blue-950/50 dark:text-blue-400 dark:border-blue-900",
  finance:       "bg-emerald-50 text-emerald-600 border-emerald-100 dark:bg-emerald-950/50 dark:text-emerald-400 dark:border-emerald-900",
  development:   "bg-violet-50 text-violet-600 border-violet-100 dark:bg-violet-950/50 dark:text-violet-400 dark:border-violet-900",
  entertainment: "bg-rose-50 text-rose-600 border-rose-100 dark:bg-rose-950/50 dark:text-rose-400 dark:border-rose-900",
  science:       "bg-amber-50 text-amber-600 border-amber-100 dark:bg-amber-950/50 dark:text-amber-400 dark:border-amber-900",
  games:         "bg-fuchsia-50 text-fuchsia-600 border-fuchsia-100 dark:bg-fuchsia-950/50 dark:text-fuchsia-400 dark:border-fuchsia-900",
};

const categoryColor = (cat: string) =>
  CATEGORY_COLORS[cat?.toLowerCase()] ?? "bg-muted text-muted-foreground border-border";

// ── Copy button ─────────────────────────────────────────────────────────────
const CopyButton = ({ text }: { text: string }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };
  return (
    <button
      type="button"
      onClick={copy}
      aria-label="Copy"
      className="p-1.5 rounded-lg hover:bg-muted/80 text-muted-foreground hover:text-foreground transition-colors"
    >
      <AnimatePresence mode="wait" initial={false}>
        {copied ? (
          <motion.span key="check" initial={{ scale: 0.7 }} animate={{ scale: 1 }} exit={{ scale: 0.7 }} className="flex">
            <Check className="w-3.5 h-3.5 text-emerald-500" />
          </motion.span>
        ) : (
          <motion.span key="copy" initial={{ scale: 0.7 }} animate={{ scale: 1 }} exit={{ scale: 0.7 }} className="flex">
            <Copy className="w-3.5 h-3.5" />
          </motion.span>
        )}
      </AnimatePresence>
    </button>
  );
};

// ── Stat card ────────────────────────────────────────────────────────────────
const StatCard = ({
  icon: Icon,
  label,
  value,
  color,
}: { icon: React.ElementType; label: string; value: string; color: string }) => (
  <div className="glass-strong rounded-xl p-5">
    <div className={`w-9 h-9 rounded-lg ${color} flex items-center justify-center mb-3`}>
      <Icon className="w-4 h-4" />
    </div>
    <div className="text-lg font-bold text-foreground tabular-nums">{value}</div>
    <div className="text-xs text-muted-foreground mt-0.5 font-medium">{label}</div>
  </div>
);

// ── Comment action menu ───────────────────────────────────────────────────────
type CommentMenuProps = {
  onEdit: () => void;
  onDelete: () => void;
};

const CommentMenu = ({ onEdit, onDelete }: CommentMenuProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-7 h-7 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        aria-label="Comment options"
      >
        <MoreHorizontal className="w-4 h-4" />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            key="menu"
            initial={{ opacity: 0, scale: 0.95, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -4 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-8 z-50 min-w-[160px] bg-background border border-border rounded-xl shadow-lg overflow-hidden"
          >
            <button
              type="button"
              onClick={() => { setOpen(false); onEdit(); }}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors text-left"
              >
              <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
              Edit Comment
            </button>
            <div className="mx-3 h-px bg-border" />
            <button
              type="button"
              onClick={() => { setOpen(false); onDelete(); }}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors text-left"
              >
              <Trash2 className="w-3.5 h-3.5" />
              Delete Comment
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ── Config snippet step (needs own state — hooks can't live in IIFE) ─────────
function ConfigSnippetStep({
  slug,
  hostedMcpHref,
  credentialHeader,
  credentialKey,
}: {
  slug: string;
  hostedMcpHref: string;
  credentialHeader: string;
  credentialKey: string;
}) {
  const [snippetTab, setSnippetTab] = useState<"claude" | "cursor">("claude");
  const snippet = `{\n  "mcpServers": {\n    "${slug}": {\n      "url": "${hostedMcpHref}",\n      "headers": {\n        "${credentialHeader}": "Bearer YOUR_${credentialKey}_HERE"\n      }\n    }\n  }\n}`;
  const filePath = snippetTab === "claude"
    ? "~/Library/Application Support/Claude/claude_desktop_config.json"
    : ".cursor/mcp.json";

  return (
    <div className="flex items-start gap-3">
      <span className="w-6 h-6 rounded-full bg-amber-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground mb-3">Add to your AI client config</p>
        <div className="flex gap-1 mb-3 p-1 bg-muted/50 rounded-lg w-fit">
          {(["claude", "cursor"] as const).map(t => (
            <button
              key={t}
              type="button"
              onClick={() => setSnippetTab(t)}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                snippetTab === t
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t === "claude" ? "Claude Desktop" : "Cursor"}
            </button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mb-2">
          Open <code className="bg-muted px-1 rounded text-xs">{filePath}</code> and add:
        </p>
        <div className="relative">
          <pre className="text-xs font-mono bg-background border border-border rounded-xl p-4 overflow-x-auto text-foreground leading-relaxed">
            {snippet}
          </pre>
          <div className="absolute top-2 right-2">
            <CopyButton text={snippet} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
const McpDetailsPage = () => {
  const { slug = "" } = useParams();
  const fallbackMcp = useMemo(() => getMcpBySlug(slug), [slug]);
  const { data: details, isLoading, error: detailsError } = useRegistryDetails(slug);
  const starMutation = useToggleRegistryStar(slug);
  const commentMutation = usePostRegistryComment(slug);
  const editCommentMutation = useEditRegistryComment(slug);
  const deleteCommentMutation = useDeleteRegistryComment(slug);

  const [commentText, setCommentText] = useState("");
  const [sessionUser, setSessionUser] = useState<SessionUser | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [personalMcpUrl, setPersonalMcpUrl] = useState<string | null>(null);
  // Per-comment edit state: { [commentId]: string } — undefined means not editing
  const [editingComments, setEditingComments] = useState<Record<string, string>>({});

  const detailsErrorMessage =
    detailsError instanceof Error
      ? detailsError.message
      : detailsError
      ? "Failed to load MCP details"
      : null;
  const error =
    actionError || (detailsErrorMessage && !fallbackMcp ? detailsErrorMessage : null);

  useEffect(() => {
    let mounted = true;
    void fetchSessionUser().then((user) => { if (mounted) setSessionUser(user); });
    return () => { mounted = false; };
  }, []);

  const mcp = useMemo(() => {
    if (details) return details;
    if (!fallbackMcp) return null;
    return {
      id: fallbackMcp.slug,
      name: fallbackMcp.name,
      slug: fallbackMcp.slug,
      description: fallbackMcp.description,
      category: fallbackMcp.category,
      icon: fallbackMcp.icon,
      fileUrl: fallbackMcp.downloadUrl,
      downloadCount: fallbackMcp.downloads,
      starCount: fallbackMcp.stars,
      createdAt: fallbackMcp.createdAt,
      updatedAt: fallbackMcp.updatedAt,
      isStarred: false,
      comments: [],
      endpointCount: fallbackMcp.endpointCount ?? 0,
    };
  }, [details, fallbackMcp]);

  const handleStar = () => {
    setActionError(null);
    starMutation.mutate(undefined, {
      onError: (err) => setActionError(err instanceof Error ? err.message : "Failed to toggle star"),
    });
  };

  const handlePostComment = () => {
    const content = commentText.trim();
    if (!content || !sessionUser?.email) return;
    setActionError(null);
    commentMutation.mutate(
      { content, user: sessionUser },
      {
        onSuccess: () => setCommentText(""),
        onError: (err) => setActionError(err instanceof Error ? err.message : "Failed to post comment"),
      },
    );
  };

  const startEdit = (commentId: string, currentContent: string) => {
    setEditingComments((prev) => ({ ...prev, [commentId]: currentContent }));
  };

  const cancelEdit = (commentId: string) => {
    setEditingComments((prev) => {
      const next = { ...prev };
      delete next[commentId];
      return next;
    });
  };

  const submitEdit = (commentId: string) => {
    const content = editingComments[commentId]?.trim();
    if (!content || !sessionUser) return;
    setActionError(null);
    editCommentMutation.mutate(
      { commentId, content, user: sessionUser },
      {
        onSuccess: () => cancelEdit(commentId),
        onError: (err) => setActionError(err instanceof Error ? err.message : "Failed to edit comment"),
      },
    );
  };

  const handleDelete = (commentId: string) => {
    if (!sessionUser?.email) return;
    setActionError(null);
    deleteCommentMutation.mutate(
      { commentId, user: sessionUser },
      {
        onSuccess: () => setActionError(null),
        onError: (err) => setActionError(err instanceof Error ? err.message : "Failed to delete comment"),
      },
    );
  };

  const formatDate = (value: string) =>
    new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

  // ── Loading skeleton ──────────────────────────────────────────────────────
  if (isLoading && !mcp) {
    return (
      <>
        <TopHeader />
        <main className="max-w-5xl mx-auto px-6 py-12 space-y-4 animate-pulse">
          <div className="h-5 w-32 bg-muted rounded-lg" />
          <div className="h-36 bg-muted rounded-2xl" />
          <div className="h-52 bg-muted rounded-2xl" />
        </main>
      </>
    );
  }

  // ── Not found ──────────────────────────────────────────────────────────────
  if (!mcp) {
    return (
      <>
        <TopHeader />
        <main className="max-w-5xl mx-auto px-6 py-16 text-center">
          <div className="text-5xl mb-4">🔍</div>
          <h1 className="text-2xl font-bold mb-2 text-foreground">MCP not found</h1>
          <p className="text-muted-foreground mb-6">
            The MCP you requested doesn't exist in the registry.
          </p>
          <Link
            to="/registry"
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Registry
          </Link>
        </main>
      </>
    );
  }

  const hostedMcpHref = resolveHostedMcpHref(details ?? null, fallbackMcp);
  const isLinkOnly = Boolean(hostedMcpHref);
  const authType = details?.authType ?? (fallbackMcp as { authType?: string } | undefined)?.authType;
  const requiresAuth = Boolean(authType && authType !== "none");
  const authLabel = details?.authLabel ?? "";
  const authInstructions = details?.authInstructions ?? "";
  const credentialHeader = details?.credentialHeader ?? "Authorization";
  const credentialKey = details?.credentialKey ?? "YOUR_API_KEY";
  const features =
    details?.features && details.features.length > 0 ? details.features : fallbackMcp?.features ?? [];
  const visibleFeatures = showAllFeatures ? features : features.slice(0, 5);
  const quickStartSteps = fallbackMcp?.quickStartSteps ?? [];
  const setupGuideUrl =
    fallbackMcp?.setupGuideUrl ?? "https://docs.anthropic.com/en/docs/claude-desktop/mcp";
  const creator = fallbackMcp?.creator ?? "@apitomcp";
  const endpointCount = (mcp as { endpointCount?: number }).endpointCount ?? 0;

  return (
    <>
      <TopHeader />
      <main className="min-h-screen">
        {/* ── Hero header ─────────────────────────────────────────────────── */}
        <div className="relative overflow-hidden border-b border-border/50 bg-background">
          <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" />
          <motion.div
            animate={{ x: [0, 18, 0], y: [0, -10, 0] }}
            transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-0 right-0 w-[360px] h-[260px] rounded-full pointer-events-none"
            style={{ background: "radial-gradient(circle, hsl(214 100% 93% / 0.55) 0%, transparent 70%)" }}
          />

          <div className="max-w-5xl mx-auto px-6 pt-10 pb-10 relative z-10">
            {/* Breadcrumb */}
            <motion.div
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.35 }}
              className="mb-6"
            >
              <Link
                to="/registry"
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors group"
              >
                <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" />
                Registry
              </Link>
            </motion.div>

            {/* Title row */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="flex flex-col sm:flex-row sm:items-start justify-between gap-5"
            >
              <div className="flex items-start gap-4">
                {mcp.icon && (
                  <div className="w-16 h-16 rounded-2xl bg-muted/70 flex items-center justify-center text-4xl flex-shrink-0 shadow-sm">
                    {mcp.icon}
                  </div>
                )}
                <div>
                  <div className="flex items-center gap-2 flex-wrap mb-1.5">
                    <h1 className="text-2xl md:text-3xl font-bold text-foreground tracking-tight">
                      {mcp.name}
                    </h1>
                    {mcp.category && (
                      <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border capitalize ${categoryColor(mcp.category)}`}>
                        {mcp.category}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed max-w-xl">
                    {mcp.description}
                  </p>
                </div>
              </div>

              {/* Star button */}
              <motion.button
                type="button"
                onClick={handleStar}
                disabled={starMutation.isPending}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                aria-pressed={mcp.isStarred}
                className={`self-start flex-shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-semibold transition-all disabled:opacity-60 ${
                  mcp.isStarred
                    ? "bg-amber-50 border-amber-200 text-amber-600 dark:bg-amber-950/30 dark:border-amber-800 dark:text-amber-400"
                    : "bg-background border-border text-muted-foreground hover:border-amber-300 hover:text-amber-600 dark:hover:border-amber-700"
                }`}
              >
                <Star
                  className="w-4 h-4"
                  fill={mcp.isStarred ? "currentColor" : "none"}
                  strokeWidth={mcp.isStarred ? 0 : 2}
                />
                {mcp.isStarred ? "Starred" : "Star"}
                <span className="tabular-nums text-xs opacity-70">{mcp.starCount}</span>
              </motion.button>
            </motion.div>

            {/* Meta row — updated date + creator only */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-5 text-xs text-muted-foreground"
            >
              <span className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-primary/60" />
                Updated {formatDate(mcp.updatedAt)}
              </span>
              <span className="flex items-center gap-1.5">
                <User className="w-3.5 h-3.5 text-primary/60" />
                {creator}
              </span>
            </motion.div>

            {/* Action buttons + auth notice */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.18 }}
              className="flex flex-col gap-3 mt-6"
            >
              {isLinkOnly && hostedMcpHref ? (
                <>
                  {/* MCP URL bar — only show for public MCPs or after connecting */}
                  {(!requiresAuth || personalMcpUrl) && (
                    <div className="flex items-center gap-2 w-full max-w-2xl glass-strong rounded-xl px-4 py-3 border border-border/60">
                      <Terminal className="w-4 h-4 text-primary flex-shrink-0" />
                      <code className="text-xs text-foreground font-mono break-all flex-1">
                        {personalMcpUrl ?? hostedMcpHref}
                      </code>
                      <CopyButton text={personalMcpUrl ?? hostedMcpHref} />
                      {!personalMcpUrl && (
                        <a
                          href={hostedMcpHref}
                          target="_blank"
                          rel="noreferrer"
                          className="flex-shrink-0 text-muted-foreground hover:text-primary transition-colors"
                          aria-label="Open in new tab"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </div>
                  )}

                  {/* Auth badge + Connect button */}
                  {requiresAuth ? (
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="flex items-center gap-2 text-xs font-medium text-amber-700 dark:text-amber-400">
                        <KeyRound className="w-3.5 h-3.5 flex-shrink-0" />
                        Needs your {authLabel} to work
                      </div>
                      {personalMcpUrl ? (
                        <div className="flex items-center gap-2 text-xs font-medium text-emerald-700 dark:text-emerald-400">
                          <Check className="w-3.5 h-3.5 flex-shrink-0" />
                          Connected! See your personal URL below.
                        </div>
                      ) : sessionUser?.email ? (
                        <motion.button
                          type="button"
                          onClick={() => setShowConnectModal(true)}
                          whileHover={{ scale: 1.03 }}
                          whileTap={{ scale: 0.97 }}
                          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-primary text-white text-xs font-semibold transition-all"
                        >
                          <Link2 className="w-3.5 h-3.5" />
                          Connect {mcp.name}
                        </motion.button>
                      ) : (
                        <span className="text-xs text-muted-foreground">Sign in to connect</span>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-xs font-medium text-emerald-700 dark:text-emerald-400">
                      <Globe className="w-3.5 h-3.5 flex-shrink-0" />
                      No authentication needed — use the URL directly
                    </div>
                  )}
                </>
              ) : (
                <motion.a
                  href={setupGuideUrl}
                  target="_blank"
                  rel="noreferrer"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-border bg-background hover:bg-accent text-sm font-semibold text-foreground transition-all w-fit"
                >
                  <BookOpen className="w-4 h-4" />
                  Setup Guide
                </motion.a>
              )}
            </motion.div>
          </div>
        </div>

        {/* ── Body sections ─────────────────────────────────────────────────── */}
        <div className="max-w-5xl mx-auto px-6 py-10 space-y-6">
          {isLoading && (
            <p className="text-sm text-muted-foreground">Refreshing details…</p>
          )}
          {error && (
            <div className="text-sm text-red-500 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-xl px-4 py-3">
              {error}
            </div>
          )}

          {/* Stats row — Endpoints + Updated + Stars */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-2 md:grid-cols-3 gap-4"
          >
            <StatCard
              icon={Zap}
              label="Endpoints"
              value={endpointCount > 0 ? String(endpointCount) : "—"}
              color="bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-400"
            />
            <StatCard
              icon={Calendar}
              label="Last Updated"
              value={formatDate(mcp.updatedAt)}
              color="bg-violet-50 text-violet-600 dark:bg-violet-950 dark:text-violet-400"
            />
            <StatCard
              icon={Star}
              label="Stars"
              value={String(mcp.starCount)}
              color="bg-amber-50 text-amber-600 dark:bg-amber-950 dark:text-amber-400"
            />
          </motion.div>

          {/* ── Personal MCP URL (after successful connect) ───────────────── */}
          {personalMcpUrl && (
            <motion.section
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="rounded-2xl border border-emerald-200 dark:border-emerald-900 bg-emerald-50/60 dark:bg-emerald-950/20 p-7"
            >
              <h2 className="text-base font-bold text-foreground mb-1 flex items-center gap-2">
                <Check className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                Your Personal MCP URL
              </h2>
              <p className="text-sm text-muted-foreground mb-4">
                This URL is private to you — it already has your token injected. Just paste it into Claude Desktop.
              </p>
              <div className="flex items-center gap-2 bg-background border border-border rounded-xl px-4 py-3 mb-3">
                <code className="flex-1 text-xs font-mono text-foreground break-all">{personalMcpUrl}</code>
                <CopyButton text={personalMcpUrl} />
              </div>
              <div className="relative">
                <pre className="text-xs font-mono bg-background border border-border rounded-xl p-4 overflow-x-auto text-foreground leading-relaxed">
{`{
  "mcpServers": {
    "${mcp.slug}": {
      "url": "${personalMcpUrl}"
    }
  }
}`}
                </pre>
                <div className="absolute top-2 right-2">
                  <CopyButton text={`{\n  "mcpServers": {\n    "${mcp.slug}": {\n      "url": "${personalMcpUrl}"\n    }\n  }\n}`} />
                </div>
              </div>
            </motion.section>
          )}

          {/* ── Authentication Setup Panel ─────────────────────────────────── */}
          {requiresAuth && isLinkOnly && hostedMcpHref && !personalMcpUrl && (
            <motion.section
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="rounded-2xl border border-amber-200 dark:border-amber-900 bg-amber-50/60 dark:bg-amber-950/20 p-7"
            >
              <h2 className="text-base font-bold text-foreground mb-1 flex items-center gap-2">
                <KeyRound className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                Authentication Required
              </h2>
              <p className="text-sm text-muted-foreground mb-5">
                This MCP needs your <strong className="text-foreground">{authLabel}</strong> to make API calls on your behalf.
                {authInstructions && (
                  <> {authInstructions}</>
                )}
              </p>

              {/* Step 1 — Get your token */}
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-amber-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
                  <div>
                    <p className="text-sm font-semibold text-foreground">Get your {authLabel}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Follow the instructions above to obtain your API token or key.</p>
                  </div>
                </div>

                {/* Step 2 — Quickstart snippet with Claude Desktop / Cursor tabs */}
                <ConfigSnippetStep
                  slug={mcp.slug}
                  hostedMcpHref={hostedMcpHref}
                  credentialHeader={credentialHeader}
                  credentialKey={credentialKey}
                />

                {/* Step 3 — Direct HTTP usage */}
                <div className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-amber-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground mb-2">Or use directly with any MCP client</p>
                    <p className="text-xs text-muted-foreground mb-2">Pass your token as a request header:</p>
                    <div className="relative">
                      <pre className="text-xs font-mono bg-background border border-border rounded-xl p-3 overflow-x-auto text-foreground">
{`${credentialHeader}: Bearer YOUR_${credentialKey}_HERE`}
                      </pre>
                      <div className="absolute top-1.5 right-2">
                        <CopyButton text={`${credentialHeader}: Bearer YOUR_${credentialKey}_HERE`} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Warning note */}
              <div className="mt-5 flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-100/60 dark:bg-amber-950/40 rounded-xl px-4 py-3 border border-amber-200 dark:border-amber-900">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>Never share your API token publicly. The MCP URL above is shared, but only works when a valid token is provided per-request.</span>
              </div>
            </motion.section>
          )}

          {/* Features */}
          {features.length > 0 && (
            <motion.section
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="glass-strong rounded-2xl p-7 shadow-premium"
            >
              <h2 className="text-base font-bold text-foreground mb-5 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-primary" />
                Features
              </h2>
              <ul className="space-y-3">
                {visibleFeatures.map((feature) => (
                  <li key={feature} className="flex items-start gap-3 text-sm text-foreground">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-primary" />
                    </span>
                    {feature}
                  </li>
                ))}
              </ul>
              {features.length > 5 && (
                <button
                  type="button"
                  onClick={() => setShowAllFeatures((v) => !v)}
                  className="mt-5 flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                >
                  {showAllFeatures ? "Show less" : `Show all ${features.length} features`}
                  <ChevronDown className={`w-4 h-4 transition-transform ${showAllFeatures ? "rotate-180" : ""}`} />
                </button>
              )}
            </motion.section>
          )}

          {/* Quick Start */}
          {!isLinkOnly && quickStartSteps.length > 0 && (
            <motion.section
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="glass-strong rounded-2xl p-7 shadow-premium"
            >
              <h2 className="text-base font-bold text-foreground mb-5 flex items-center gap-2">
                <Terminal className="w-5 h-5 text-primary" />
                Quick Start
              </h2>
              <p className="text-sm text-muted-foreground mb-5">
                Add this MCP to Claude with the{" "}
                <a
                  href={setupGuideUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary hover:underline inline-flex items-center gap-1"
                >
                  setup guide <ExternalLink className="w-3 h-3" />
                </a>
              </p>
              <ol className="space-y-3">
                {quickStartSteps.map((step, index) => (
                  <li key={step} className="flex items-start gap-3 text-sm text-foreground">
                    <span className="mt-0.5 w-6 h-6 rounded-full bg-primary flex items-center justify-center flex-shrink-0 text-white text-xs font-bold">
                      {index + 1}
                    </span>
                    {step}
                  </li>
                ))}
              </ol>
            </motion.section>
          )}

          {/* Comments */}
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="glass-strong rounded-2xl p-7 shadow-premium"
          >
            <h2 className="text-base font-bold text-foreground mb-6 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-primary" />
              Comments
              <span className="text-sm font-normal text-muted-foreground">
                ({mcp.comments.length})
              </span>
            </h2>

            {mcp.comments.length > 0 ? (
              <div className="space-y-4 mb-6">
                {mcp.comments.map((comment) => {
                  const isEditing = editingComments[comment.id] !== undefined;
                  const sessionEmail = sessionUser?.email?.trim().toLowerCase();
                  const isOwner = Boolean(
                    sessionEmail &&
                      (comment.user.email?.trim().toLowerCase() === sessionEmail ||
                        comment.user.id?.trim().toLowerCase() === sessionEmail),
                  );

                  return (
                    <div
                      key={comment.id}
                      className="border border-border/60 rounded-xl p-5 bg-background/50"
                    >
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-primary/15 flex items-center justify-center text-primary text-xs font-bold">
                            {(comment.user.name || comment.user.email || "U")
                              .slice(0, 1)
                              .toUpperCase()}
                          </div>
                          <span className="text-sm font-semibold text-foreground">
                            @{comment.user.name ||
                              comment.user.email?.split("@")[0] ||
                              "user"}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">
                            {formatDate(comment.createdAt)}
                          </span>
                          {/* Three-dot menu — only shown for comment owner */}
                          {isOwner && !isEditing && (
                            <CommentMenu
                              onEdit={() => startEdit(comment.id, comment.content)}
                              onDelete={() => handleDelete(comment.id)}
                            />
                          )}
                        </div>
                      </div>

                      {/* Inline edit mode */}
                      {isEditing ? (
                        <div className="space-y-2">
                          <textarea
                            rows={3}
                            value={editingComments[comment.id]}
                            onChange={(e) =>
                              setEditingComments((prev) => ({ ...prev, [comment.id]: e.target.value }))
                            }
                            className="w-full rounded-xl border border-border bg-background/60 px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 resize-none focus:outline-none focus:ring-2 focus:ring-ring/25 transition"
                          />
                          <div className="flex gap-2 justify-end">
                            <button
                              type="button"
                              onClick={() => cancelEdit(comment.id)}
                              className="px-4 py-1.5 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground transition-colors"
                            >
                              Cancel
                            </button>
                            <motion.button
                              type="button"
                              onClick={() => submitEdit(comment.id)}
                              disabled={
                                editCommentMutation.isPending ||
                                !editingComments[comment.id]?.trim()
                              }
                              whileHover={{ scale: 1.03 }}
                              whileTap={{ scale: 0.97 }}
                              className="px-4 py-1.5 rounded-lg btn-neu-primary text-sm transition-all disabled:opacity-50 disabled:pointer-events-none"
                            >
                              {editCommentMutation.isPending ? "Saving…" : "Save"}
                            </motion.button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <p className="text-sm text-foreground leading-relaxed mb-3">
                            {comment.content}
                          </p>
                          <button
                            type="button"
                            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                          >
                            <ThumbsUp className="w-3.5 h-3.5" />
                            {comment.upvotes}
                          </button>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground mb-6">
                <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-25" />
                <p className="text-sm font-medium">No comments yet</p>
                <p className="text-xs mt-1">Be the first to share your experience</p>
              </div>
            )}

            {/* Comment form */}
            <div className={mcp.comments.length > 0 ? "border-t border-border/50 pt-6" : undefined}>
              {!sessionUser?.email && (
                <p className="text-sm text-muted-foreground mb-4 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Sign in from the header to leave a comment.
                </p>
              )}
              <textarea
                id="comment"
                rows={3}
                placeholder={
                  sessionUser?.email
                    ? "Share your experience with this MCP…"
                    : "Sign in to comment"
                }
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                disabled={!sessionUser?.email}
                className="w-full rounded-xl border border-border bg-background/60 px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 resize-none focus:outline-none focus:ring-2 focus:ring-ring/25 transition disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <div className="flex justify-end mt-3">
                <motion.button
                  type="button"
                  disabled={
                    commentMutation.isPending || !commentText.trim() || !sessionUser?.email
                  }
                  onClick={handlePostComment}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="px-5 py-2.5 rounded-xl btn-neu-primary text-sm transition-all disabled:opacity-50 disabled:pointer-events-none"
                >
                  {commentMutation.isPending ? "Posting…" : "Post Comment"}
                </motion.button>
              </div>
            </div>
          </motion.section>
        </div>
      </main>
      <Footer />

      {/* ── Connect modal ─────────────────────────────────────────────────── */}
      <AnimatePresence>
        {showConnectModal && sessionUser && details && requiresAuth && (
          <RegistryConnectModal
            mcp={{
              slug: mcp.slug,
              name: mcp.name,
              icon: mcp.icon,
              authLabel: authLabel || undefined,
              authInstructions: authInstructions || undefined,
              credentialKey: credentialKey || undefined,
            }}
            user={sessionUser}
            onClose={() => setShowConnectModal(false)}
            onConnected={(url) => {
              setPersonalMcpUrl(url);
              setShowConnectModal(false);
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default McpDetailsPage;
